import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getTextStats } from '../../utils/llm-generator/markdownConverter';

/**
 * MarkdownEditor Component
 * Simple textarea-based markdown editor with section support
 */
export default function MarkdownEditor({
  sections,
  onSectionsChange,
  selectedSectionId,
  onSelectSection,
  className = ''
}) {
  const [showPreview, setShowPreview] = useState(false);

  // Get currently selected section
  const currentSection = useMemo(() => {
    return sections.find(s => s.id === selectedSectionId) || sections[0];
  }, [sections, selectedSectionId]);

  // Calculate stats for current section
  const currentStats = useMemo(() => {
    return currentSection ? getTextStats(currentSection.content) : null;
  }, [currentSection]);

  // Calculate total stats
  const totalStats = useMemo(() => {
    const allContent = sections.map(s => s.content).join('\n\n');
    return getTextStats(allContent);
  }, [sections]);

  // Handle content change for current section
  const handleContentChange = useCallback((newContent) => {
    const updatedSections = sections.map(s => 
      s.id === selectedSectionId 
        ? { ...s, content: newContent }
        : s
    );
    onSectionsChange(updatedSections);
  }, [sections, selectedSectionId, onSectionsChange]);

  // Delete a section
  const handleDeleteSection = useCallback((sectionId) => {
    if (sections.length <= 1) return;
    const updatedSections = sections.filter(s => s.id !== sectionId);
    onSectionsChange(updatedSections);
    if (selectedSectionId === sectionId) {
      onSelectSection(updatedSections[0]?.id);
    }
  }, [sections, selectedSectionId, onSectionsChange, onSelectSection]);

  // Move section up/down
  const handleMoveSection = useCallback((sectionId, direction) => {
    const index = sections.findIndex(s => s.id === sectionId);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    const updatedSections = [...sections];
    [updatedSections[index], updatedSections[newIndex]] = [updatedSections[newIndex], updatedSections[index]];
    onSectionsChange(updatedSections);
  }, [sections, onSectionsChange]);

  // Simple markdown preview renderer
  const renderPreview = useCallback((content) => {
    if (!content) return '<em class="text-gray-400">Kein Inhalt</em>';
    
    return content
      // Headers
      .replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold mt-6 mb-3">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-6 mb-4">$1</h1>')
      // Code blocks
      .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre class="bg-gray-900 text-gray-100 p-3 rounded my-2 overflow-x-auto text-sm"><code>$2</code></pre>')
      // Inline code
      .replace(/`([^`]+)`/g, '<code class="bg-gray-200 dark:bg-gray-700 px-1 rounded text-sm">$1</code>')
      // Bold
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      // Lists
      .replace(/^- (.+)$/gm, '<li class="ml-4">$1</li>')
      .replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4">$1. $2</li>')
      // Paragraphs
      .replace(/\n\n/g, '</p><p class="my-2">')
      // Wrap in paragraph
      .replace(/^/, '<p class="my-2">')
      .replace(/$/, '</p>');
  }, []);

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
          ✏️ Markdown Editor
          <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
            {sections.length} Abschnitte
          </span>
        </h3>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className={`text-xs px-2 py-1 rounded theme-transition ${
              showPreview 
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' 
                : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            👁️ {showPreview ? 'Editor' : 'Vorschau'}
          </button>
        </div>
      </div>

      <div className="flex h-96">
        {/* Section sidebar */}
        <div className="w-48 border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-gray-50 dark:bg-gray-900/50">
          {sections.map((section, index) => (
            <div
              key={section.id}
              className={`group relative ${
                section.id === selectedSectionId 
                  ? 'bg-primary-50 dark:bg-primary-900/20 border-l-2 border-primary-500' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <button
                onClick={() => onSelectSection(section.id)}
                className="w-full text-left px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">
                    {'#'.repeat(section.level || 1)}
                  </span>
                  <span className={`text-sm truncate ${
                    section.id === selectedSectionId 
                      ? 'font-medium text-primary-700 dark:text-primary-300' 
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {section.title || `Abschnitt ${index + 1}`}
                  </span>
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {getTextStats(section.content).words} Wörter
                </div>
              </button>
              
              {/* Section actions (visible on hover) */}
              <div className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex gap-0.5">
                <button
                  onClick={(e) => { e.stopPropagation(); handleMoveSection(section.id, 'up'); }}
                  disabled={index === 0}
                  className="p-1 text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  title="Nach oben"
                >
                  ↑
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleMoveSection(section.id, 'down'); }}
                  disabled={index === sections.length - 1}
                  className="p-1 text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  title="Nach unten"
                >
                  ↓
                </button>
                {sections.length > 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteSection(section.id); }}
                    className="p-1 text-xs text-gray-400 hover:text-danger-600"
                    title="Löschen"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Editor/Preview area */}
        <div className="flex-1 flex flex-col">
          {/* Section header */}
          {currentSection && (
            <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
              <input
                type="text"
                value={currentSection.title}
                onChange={(e) => {
                  const updatedSections = sections.map(s =>
                    s.id === selectedSectionId
                      ? { ...s, title: e.target.value, headerLine: `${'#'.repeat(s.level || 1)} ${e.target.value}` }
                      : s
                  );
                  onSectionsChange(updatedSections);
                }}
                className="w-full text-sm font-medium bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-400"
                placeholder="Abschnittstitel..."
              />
            </div>
          )}

          {/* Content area */}
          <div className="flex-1 relative">
            {showPreview ? (
              /* Preview mode */
              <div 
                className="absolute inset-0 p-4 overflow-auto prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ 
                  __html: currentSection ? renderPreview(currentSection.content) : '' 
                }}
              />
            ) : (
              /* Editor mode */
              <textarea
                value={currentSection?.content || ''}
                onChange={(e) => handleContentChange(e.target.value)}
                className="absolute inset-0 w-full h-full p-4 resize-none border-none outline-none bg-transparent text-sm font-mono text-gray-800 dark:text-gray-200 placeholder-gray-400"
                placeholder="Markdown-Inhalt hier eingeben..."
                spellCheck={false}
              />
            )}
          </div>

          {/* Stats footer */}
          <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <div className="flex gap-4">
              {currentStats && (
                <>
                  <span>{currentStats.words} Wörter</span>
                  <span>{currentStats.chars.toLocaleString()} Zeichen</span>
                  <span>~{currentStats.estimatedTokens.toLocaleString()} Tokens</span>
                </>
              )}
            </div>
            <div>
              Gesamt: {totalStats.words} Wörter / ~{totalStats.estimatedTokens.toLocaleString()} Tokens
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

MarkdownEditor.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    level: PropTypes.number,
    headerLine: PropTypes.string
  })).isRequired,
  onSectionsChange: PropTypes.func.isRequired,
  selectedSectionId: PropTypes.string,
  onSelectSection: PropTypes.func.isRequired,
  className: PropTypes.string
};
